<!-- Секция демонстрации приложения - Organism -->
<template>
  <section class="relative bg-black py-16 lg:py-24" id="app-showcase">
    <!-- убери overflow-hidden ↑ -->

    <div class="relative z-10 mx-auto max-w-7xl px-6">
      <!-- Первый блок: iPhone -->
      <div class="grid grid-cols-1 lg:grid-cols-2 grid-rows-1 gap-4 mb-12 lg:mb-0" ref="firstBlockRef">
        <div class="flex flex-col justify-center order-1 lg:order-1 app-block-item app-text-left">
          <h2 class="app-title">
            Максимум удобства в мини-приложении Telegram
          </h2>

          <ul class="mt-6 flex flex-col gap-[20px]">
            <li v-for="(item, index) in leftFeatures" :key="`feature-${index}`" class="flex items-center gap-3">
              <span class="inline-flex h-[15px] w-[15px] rounded-full border border-white/70"></span>
              <span class="text-[15px] leading-relaxed text-[#ececec] font-body">{{ item }}</span>
            </li>
          </ul>
        </div>

        <div class="relative flex justify-center lg:justify-end order-2 lg:order-2 app-block-item app-phone-right">
          <div class="pointer-events-none absolute right-0 top-1/2 rounded-full bg-[#ff3f1a]"
            style="width: 48rem; height: 48rem; filter: blur(160px); opacity: 0.4; transform: translate(50%, -50%) translateZ(0);">
          </div>
          <img :src="iphoneFrame" alt="iphone" class="relative z-10 object-contain max-h-[600px] lg:max-h-[910px]" />
        </div>
      </div>

      <!-- Второй блок: Android -->
      <div class="grid grid-cols-1 lg:grid-cols-2 grid-rows-1 gap-4" ref="secondBlockRef">
        <!-- На мобильной версии текст идет первым (order-1), на десктопе - вторым -->
        <div class="flex flex-col justify-center order-1 lg:order-2 app-block-item app-text-right">
          <h2 class="app-title">
            Максимум удобства в мини-приложении Telegram
          </h2>

          <ul class="mt-6 flex flex-col gap-[20px]">
            <li v-for="(item, index) in rightFeatures" :key="`feature-${index}`" class="flex items-center gap-3">
              <span class="inline-flex h-[15px] w-[15px] rounded-full border border-white/70"></span>
              <span class="text-[15px] leading-relaxed text-[#ececec] font-body">{{ item }}</span>
            </li>
          </ul>
        </div>

        <!-- На мобильной версии телефон идет вторым (order-2), на десктопе - первым -->
        <div class="relative flex justify-center lg:justify-start order-2 lg:order-1 app-block-item app-phone-left">
          <div class="pointer-events-none absolute left-0 top-1/2 rounded-full bg-[#97D6DF]"
            style="width: 48rem; height: 48rem; filter: blur(160px); opacity: 0.4; transform: translate(-50%, -50%) translateZ(0);">
          </div>
          <img :src="androidFrame" alt="android" class="relative z-10 object-contain max-h-[600px] lg:max-h-[910px]" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const leftFeatures = [
  'Автоматизированный сервис 24/7',
  'Понятный интерфейс',
  'Стабильная работа на разных устройствах',
  'Твой аккаунт - ключ к миру музыки',
  'Мобильность использования'
]

const rightFeatures = [
  'Ежедневная статистика',
  'Калькулятор роялти',
  'Вывод средств удобным способом',
  'Широкий базовый функционал',
  'Ротация треков на радио'
]

const iphoneFrame = ref('/images/block/app/iphone.png')
const androidFrame = ref('/images/block/app/android.png')

// Refs для блоков
const firstBlockRef = ref<HTMLElement | null>(null)
const secondBlockRef = ref<HTMLElement | null>(null)

// Флаги анимации
const isFirstBlockAnimated = ref(false)
const isSecondBlockAnimated = ref(false)

let firstObserver: IntersectionObserver | null = null
let secondObserver: IntersectionObserver | null = null

onMounted(() => {
  // Наблюдатель для первого блока (текст слева, телефон справа)
  firstObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isFirstBlockAnimated.value) {
          console.log('✨ [AppShowcase] First block is visible - triggering animations')
          isFirstBlockAnimated.value = true
          
          const textLeft = entry.target.querySelector('.app-text-left')
          const phoneRight = entry.target.querySelector('.app-phone-right')
          
          textLeft?.classList.add('animate-active')
          phoneRight?.classList.add('animate-active')
        }
      })
    },
    {
      threshold: 0.2,
      rootMargin: '0px'
    }
  )

  // Наблюдатель для второго блока (телефон слева, текст справа)
  secondObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isSecondBlockAnimated.value) {
          console.log('✨ [AppShowcase] Second block is visible - triggering animations')
          isSecondBlockAnimated.value = true
          
          const textRight = entry.target.querySelector('.app-text-right')
          const phoneLeft = entry.target.querySelector('.app-phone-left')
          
          textRight?.classList.add('animate-active')
          phoneLeft?.classList.add('animate-active')
        }
      })
    },
    {
      threshold: 0.2,
      rootMargin: '0px'
    }
  )

  if (firstBlockRef.value) {
    firstObserver.observe(firstBlockRef.value)
  }

  if (secondBlockRef.value) {
    secondObserver.observe(secondBlockRef.value)
  }
})

onUnmounted(() => {
  if (firstObserver) {
    firstObserver.disconnect()
  }
  if (secondObserver) {
    secondObserver.disconnect()
  }
})
</script>

<style scoped>
.app-title {
  font-family: 'Halvar Breitschrift', sans-serif;
  font-size: 40px;
  font-weight: 500;
  line-height: 0.95;
  background: linear-gradient(109deg, #ececec 52.01%, rgba(236, 236, 236, 0) 135.73%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

li span {
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  padding-left: 12px;
}

/* Анимации для блоков приложения */
@keyframes app-fade-in-left {
  from {
    opacity: 0;
    transform: translateX(-80px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes app-fade-in-right {
  from {
    opacity: 0;
    transform: translateX(80px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Начальное состояние - элементы скрыты только на десктопе */
@media (min-width: 1024px) {
  .app-block-item {
    opacity: 0;
    animation-fill-mode: forwards;
    animation-play-state: paused;
  }

  /* Текст слева выезжает слева */
  .app-block-item.animate-active.app-text-left {
    animation: app-fade-in-left 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    animation-play-state: running;
  }

  /* Телефон справа выезжает справа */
  .app-block-item.animate-active.app-phone-right {
    animation: app-fade-in-right 1s cubic-bezier(0.4, 0, 0.2, 1) 0.2s forwards;
    animation-play-state: running;
  }

  /* Телефон слева выезжает слева */
  .app-block-item.animate-active.app-phone-left {
    animation: app-fade-in-left 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    animation-play-state: running;
  }

  /* Текст справа выезжает справа */
  .app-block-item.animate-active.app-text-right {
    animation: app-fade-in-right 1s cubic-bezier(0.4, 0, 0.2, 1) 0.2s forwards;
    animation-play-state: running;
  }
}

/* На мобильной версии анимации не нужны - элементы видны сразу */
@media (max-width: 1023px) {
  .app-block-item {
    opacity: 1;
  }
}
</style>
